/**
 * Best-effort Event page-view telemetry (KAN-460).
 *
 * The gateway owns visitor identity (`gruv_visitor_id` HttpOnly cookie). This
 * helper only sends browser-known context plus a per-page-load `viewId` so
 * React remount / effect retries collapse to one Reports row.
 *
 * Analytics failure must never block rendering, auth, or checkout.
 */

import { ANALYTICS_EVENT_TRAFFIC_FLAG, isFeatureEnabled, loadConfig } from "@project-x/config";
import { hasAcceptedAnalyticsConsent, isAnalyticsConsentUiEnabled } from "./analyticsConsent";
import { resolvePromoCodeForClient } from "./promoAttribution";

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const INGEST_PATH = "/api/analytics/event-views";
const MAX_REFERRER = 512;
const MAX_UTM = 128;

/** Browser-owned Event view payload. Do not include IP or User-Agent. */
export interface EventViewBeaconPayload {
  viewId: string;
  eventId: string;
  publicSlug?: string;
  occurredAt: string;
  promoterCode?: string;
  referrer?: string;
  utm?: {
    source?: string;
    medium?: string;
    campaign?: string;
    content?: string;
    term?: string;
  };
}

function newViewId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return "00000000-0000-4000-8000-000000000000";
}

/**
 * Returns a stable viewId for this Event + document load.
 * `performance.timeOrigin` is constant across React remounts in the same navigation.
 */
export function viewIdForPageLoad(
  eventId: string,
  storage: Pick<Storage, "getItem" | "setItem"> | null = typeof sessionStorage === "undefined" ? null : sessionStorage,
  timeOrigin: number = typeof performance === "undefined" ? Date.now() : performance.timeOrigin,
): string {
  const key = `gruv_event_view_id:${eventId}:${String(Math.trunc(timeOrigin))}`;
  if (!storage) return newViewId();
  try {
    const existing = storage.getItem(key);
    if (existing && UUID_PATTERN.test(existing)) return existing;
    const id = newViewId();
    storage.setItem(key, id);
    return id;
  } catch {
    return newViewId();
  }
}

function boundedQuery(params: URLSearchParams, key: string): string | undefined {
  const value = params.get(key)?.trim();
  if (!value || value.length > MAX_UTM || /[\r\n]/.test(value)) return undefined;
  return value;
}

/** Collects UTM query params when present. Never includes promoter. */
export function utmFromSearchParams(params: URLSearchParams): EventViewBeaconPayload["utm"] {
  const utm = {
    source: boundedQuery(params, "utm_source"),
    medium: boundedQuery(params, "utm_medium"),
    campaign: boundedQuery(params, "utm_campaign"),
    content: boundedQuery(params, "utm_content"),
    term: boundedQuery(params, "utm_term"),
  };
  return Object.values(utm).some(Boolean) ? utm : undefined;
}

/**
 * Builds the ingest payload after a successful public Event hydration.
 * Returns null when the Event identity is missing (failed/non-public render).
 */
export function buildEventViewPayload(input: {
  eventId?: string | null;
  publicSlug?: string | null;
  search?: string;
  referrer?: string;
  occurredAt?: Date;
  promoterCode?: string | null;
  viewId?: string;
}): EventViewBeaconPayload | null {
  const eventId = input.eventId?.trim();
  if (!eventId || !UUID_PATTERN.test(eventId)) return null;
  const search = new URLSearchParams(input.search ?? "");
  const referrer = input.referrer?.trim();
  const fromQuery = boundedQuery(search, "promoter");
  const promoter = input.promoterCode?.trim() || fromQuery || resolvePromoCodeForClient() || undefined;
  return {
    viewId: input.viewId && UUID_PATTERN.test(input.viewId) ? input.viewId : viewIdForPageLoad(eventId),
    eventId: eventId.toLowerCase(),
    publicSlug: input.publicSlug?.trim() || undefined,
    occurredAt: (input.occurredAt ?? new Date()).toISOString(),
    promoterCode: promoter,
    referrer: referrer && referrer.length <= MAX_REFERRER ? referrer : undefined,
    utm: utmFromSearchParams(search),
  };
}

/**
 * Fire-and-forget ingest. Prefers `sendBeacon`; falls back to keepalive fetch.
 * Swallows every failure so Event UI stays usable.
 */
export function sendEventView(payload: EventViewBeaconPayload): void {
  if (typeof window === "undefined") return;
  const body = JSON.stringify(payload);
  try {
    if (typeof navigator.sendBeacon === "function") {
      const blob = new Blob([body], { type: "application/json" });
      if (navigator.sendBeacon(INGEST_PATH, blob)) return;
    }
  } catch {
    // Fall through to fetch.
  }
  try {
    void fetch(INGEST_PATH, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body,
      credentials: "same-origin",
      keepalive: true,
    }).catch(() => undefined);
  } catch {
    // Ignore.
  }
}

/** Records one view after successful Event hydration. No-ops on missing identity. */
export function recordEventPageView(input: {
  eventId?: string | null;
  publicSlug?: string | null;
  search?: string;
  referrer?: string;
  promoterCode?: string | null;
}): boolean {
  if (!isAnalyticsConsentUiEnabled()) {
    return false;
  }
  if (!isFeatureEnabled(loadConfig(), ANALYTICS_EVENT_TRAFFIC_FLAG)) {
    return false;
  }
  if (!hasAcceptedAnalyticsConsent()) {
    return false;
  }
  const payload = buildEventViewPayload({
    ...input,
    referrer: input.referrer ?? (typeof document === "undefined" ? undefined : document.referrer),
    search: input.search ?? (typeof window === "undefined" ? "" : window.location.search),
  });
  if (!payload) return false;
  sendEventView(payload);
  pushGa4EventView(payload);
  return true;
}

/**
 * Recommended GA4 custom event for cross-checking public Event traffic.
 * Destination fires through the existing GTM container; this only pushes dataLayer.
 * Never include promoter codes, IP, User-Agent, or other PII.
 */
export function pushGa4EventView(payload: EventViewBeaconPayload): void {
  if (typeof window === "undefined") return;
  const w = window as Window & { dataLayer?: Record<string, unknown>[] };
  w.dataLayer = w.dataLayer || [];
  w.dataLayer.push({
    event: "event_view",
    event_id: payload.eventId,
    public_slug: payload.publicSlug,
    platform: "web",
    source_surface: "consumer",
  });
}
